"use client"

import { useState, useEffect, useRef } from "react"
import axios from "axios"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { ChevronLeft, ChevronRight, Calendar, Star, Zap, Clock } from "lucide-react"
import { Button } from "./ui/Button"
import AdSlot from "./adslot"
import DateCalculatorModal from "./DateCalculatorModal"

// helper: convert "MM/DD/YYYY" -> "YYYY-MM-DD" for Airtable
const parseMMDDYYYYToISO = (value) => {
  if (!value) return ""
  const trimmed = value.trim()
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed)
  if (!match) return ""

  const [, m, d, y] = match
  const month = m.padStart(2, "0")
  const day = d.padStart(2, "0")
  return `${y}-${month}-${day}`
}

// helper: is "MM/DD" complete enough to filter?
const isCompleteMonthDay = (value) => {
  if (!value) return false
  const parts = value.split("/")
  if (parts.length !== 2) return false

  const [m, d] = parts.map((p) => p.trim())
  if (!m || !d) return false

  const monthNum = parseInt(m, 10)
  const dayNum = parseInt(d, 10)

  if (Number.isNaN(monthNum) || Number.isNaN(dayNum)) return false
  if (monthNum < 1 || monthNum > 12) return false
  if (dayNum < 1 || dayNum > 31) return false

  return true
}
const pad2 = (n) => String(n).padStart(2, "0")

export default function TimelineBody() {
  const navigate = useNavigate()
  const location = useLocation()
  // prevent "restore effects" from overwriting fast user input
const didRestoreRef = useRef(false)
const userInteractedRef = useRef(false)
   useEffect(() => {
    document.title = "Swift-Lore - Full Taylor Swift Timeline Archive"

    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement("meta")
      metaDescription.name = "description"
      document.head.appendChild(metaDescription)
    }

    metaDescription.content =
      "Browse the full Swift-Lore Taylor Swift timeline: thousands of dated events with filters for albums, tours, Taylor sightings, and other characters within the Taylor Swift Cinematic Universe :)."

  }, [])
  
  // Helper: Update URL params when filters change
  const updateURLParams = () => {
  const params = new URLSearchParams()

  if (sortOrder && sortOrder !== "desc") params.set("sort", sortOrder)
  if (viewMode && viewMode !== "grid") params.set("view", viewMode)
  if (filterKeywords.length > 0) params.set("keywords", filterKeywords.join(","))
  if (startDateInput) params.set("start", startDateInput)
  if (endDateInput) params.set("end", endDateInput)
  if (monthDay) params.set("monthday", monthDay)
  if (searchQuery) params.set("q", searchQuery)
  if (keywordMatchType !== "all") params.set("match", keywordMatchType)

  const queryString = params.toString()
  const basePath = window.location.pathname
  const newUrl = queryString ? `${basePath}?${queryString}` : basePath

  window.history.replaceState({}, "", newUrl)
}

  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const recordsPerPage = 12
const filterRecordsPerPage = 100
const searchRecordsPerPage = 100

  // Filter states
  const [sortOrder, setSortOrder] = useState("desc")
  const [filterKeywords, setFilterKeywords] = useState([])

  // these are what the user types, in MM/DD/YYYY
  const [startDateInput, setStartDateInput] = useState("")
  const [endDateInput, setEndDateInput] = useState("")

  const [monthDay, setMonthDay] = useState("")
  const [searchQuery, setSearchQuery] = useState("")

  // keywords
  const [allKeywords, setAllKeywords] = useState([])
  const [keywordsLoading, setKeywordsLoading] = useState(false)
  const [keywordsLoaded, setKeywordsLoaded] = useState(false)
  const [showKeywordDropdown, setShowKeywordDropdown] = useState(false)

  // keyword search + match type
  const [keywordSearchQuery, setKeywordSearchQuery] = useState("")
  const [keywordMatchType, setKeywordMatchType] = useState("all") // "any" or "all"

  // pagination offsets
  const [offsetHistory, setOffsetHistory] = useState([null])
  const [currentOffsetIndex, setCurrentOffsetIndex] = useState(0)

  // search mode
const [isSearchMode, setIsSearchMode] = useState(false)
const [searchResults, setSearchResults] = useState([])

// search pagination (separate from normal timeline pagination)
const [searchPage, setSearchPage] = useState(1)
const [searchHasMore, setSearchHasMore] = useState(false)
const [searchOffsetHistory, setSearchOffsetHistory] = useState([null])
const [searchOffsetIndex, setSearchOffsetIndex] = useState(0)

    const [isFilterMode, setIsFilterMode] = useState(false)
  const [debouncedStart, setDebouncedStart] = useState("")
const [debouncedEnd, setDebouncedEnd] = useState("")

  // Calendar state
  const [showCalendar, setShowCalendar] = useState(false)
  const [showDateCalc, setShowDateCalc] = useState(false)
  const [dateEventsMap, setDateEventsMap] = useState({})
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth())
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear())

  // view mode: "grid" or "compact"
  const [viewMode, setViewMode] = useState("grid")
  const TIMELINE_FILTERS_KEY = "swiftLoreTimelineFilters"

  // ===== Calendar Functions =====
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay()
  }

  const generateCalendar = () => {
  const daysInMonth = getDaysInMonth(calendarMonth, calendarYear)
  const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear)
  const calendar = []

  // Add empty cells for days before the first day of month
  for (let i = 0; i < firstDay; i++) {
    calendar.push(null)
  }

  // Add days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    calendar.push(i)
  }

  // 🔒 Pad out to a full 6-week (42 cell) grid
  while (calendar.length < 42) {
    calendar.push(null)
  }

  return calendar
}

  const handleDateSelect = (day) => {
    if (day) {
      const selectedMonth = calendarMonth + 1
      const selectedDay = day
      const monthStr = selectedMonth.toString().padStart(2, '0')
      const dayStr = selectedDay.toString().padStart(2, '0')
      
      // Set the monthDay input with MM/DD format
      setMonthDay(`${monthStr}/${dayStr}`)
      setShowCalendar(false)
      resetPagination()
    }
  }

  const navigateCalendarMonth = (direction) => {
    if (direction === 'prev') {
      if (calendarMonth === 0) {
        setCalendarMonth(11)
        setCalendarYear(calendarYear - 1)
      } else {
        setCalendarMonth(calendarMonth - 1)
      }
    } else {
      if (calendarMonth === 11) {
        setCalendarMonth(0)
        setCalendarYear(calendarYear + 1)
      } else {
        setCalendarMonth(calendarMonth + 1)
      }
    }
  }

  const jumpToToday = () => {
  const today = new Date()
  const monthStr = (today.getMonth() + 1).toString().padStart(2, '0')
  const dayStr = today.getDate().toString().padStart(2, '0')
  
  // Batch state updates
  setMonthDay(`${monthStr}/${dayStr}`)
  setCalendarMonth(today.getMonth())
  setCalendarYear(today.getFullYear())
  setShowCalendar(false)
  
  // Small delay to prevent double render
  setTimeout(() => {
    resetPagination()
  }, 10)
}

  const jumpToThisMonth = () => {
    const today = new Date()
    setCalendarMonth(today.getMonth())
    setCalendarYear(today.getFullYear())
  }

  const hasEvents = (day) => {
  if (!day) return false
  const dateKey = `${calendarYear}-${pad2(calendarMonth + 1)}-${pad2(day)}`
  return !!dateEventsMap[dateKey]
}

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
  // Tag click = add filter, don’t navigate
  const handleTagClick = (e, keyword) => {
    e.preventDefault()
    e.stopPropagation()

    userInteractedRef.current = true

if (!filterKeywords.includes(keyword)) {
  setFilterKeywords([...filterKeywords, keyword])
}
resetPagination()
  }
  // On first mount, restore filters from sessionStorage (per user / per tab)
  useEffect(() => {
  if (typeof window === "undefined") return
  if (didRestoreRef.current) return
  if (userInteractedRef.current) return

    const saved = window.sessionStorage.getItem(TIMELINE_FILTERS_KEY)
    if (!saved) return

    try {
      const parsed = JSON.parse(saved)

      if (parsed.sortOrder) setSortOrder(parsed.sortOrder)
      if (Array.isArray(parsed.filterKeywords)) setFilterKeywords(parsed.filterKeywords)
      if (typeof parsed.startDateInput === "string") setStartDateInput(parsed.startDateInput)
      if (typeof parsed.endDateInput === "string") setEndDateInput(parsed.endDateInput)
      if (typeof parsed.monthDay === "string") setMonthDay(parsed.monthDay)
      if (typeof parsed.searchQuery === "string") setSearchQuery(parsed.searchQuery)
      if (parsed.keywordMatchType) setKeywordMatchType(parsed.keywordMatchType)
      if (parsed.viewMode) setViewMode(parsed.viewMode)
      didRestoreRef.current = true
    } catch (e) {
      console.error("Error parsing saved timeline filters:", e)
    }
    didRestoreRef.current = true
  }, [])
  // On first mount, if the URL has filters (shared link), apply them
  useEffect(() => {
  if (typeof window === "undefined") return
  if (didRestoreRef.current) return
  if (userInteractedRef.current) return

    const urlParams = new URLSearchParams(window.location.search)

    const urlSort = urlParams.get("sort")
    const urlView = urlParams.get("view")
    const urlKeywords = urlParams.get("keywords")
    const urlStart = urlParams.get("start")
    const urlEnd = urlParams.get("end")
    const urlMonthDay = urlParams.get("monthday")
    const urlQ = urlParams.get("q")
    const urlMatch = urlParams.get("match")

    if (urlSort) setSortOrder(urlSort)
    if (urlView) setViewMode(urlView)
    if (urlKeywords) setFilterKeywords(urlKeywords.split(","))
    if (urlStart) setStartDateInput(urlStart)
    if (urlEnd) setEndDateInput(urlEnd)
    if (urlMonthDay) setMonthDay(urlMonthDay)
    if (urlQ) setSearchQuery(urlQ)
    if (urlMatch) setKeywordMatchType(urlMatch)
  }, [])

  // Read query params (?q=, ?keyword=)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const queryFromUrl = urlParams.get("q")
    const keywordFromUrl = urlParams.get("keyword")

    if (keywordFromUrl) {
      if (!filterKeywords.includes(keywordFromUrl)) {
        setFilterKeywords([keywordFromUrl])
      }
      setSearchQuery("")
      setStartDateInput("")
      setEndDateInput("")
      setMonthDay("")
      resetPagination()

      setTimeout(() => {
        urlParams.delete("keyword")
        navigate(`?${urlParams.toString()}`, { replace: true })
      }, 0)
    } else if (queryFromUrl) {
      setSearchQuery(queryFromUrl)
    }
  }, [location.search, navigate, filterKeywords])

  // lazy-load all unique keywords from Airtable when dropdown first opens
  const loadKeywordsIfNeeded = async () => {
  // Check cache first
  const cached = localStorage.getItem('swiftlore_keywords');
  const cacheTime = localStorage.getItem('swiftlore_keywords_time');
  
  // Use cache if it's less than 1 hour old
  if (cached && cacheTime && (Date.now() - parseInt(cacheTime)) < 60 * 60 * 1000) {
    setAllKeywords(JSON.parse(cached));
    setKeywordsLoaded(true);
    return;
  }

  if (keywordsLoaded || keywordsLoading) return;

  setKeywordsLoading(true);
  try {
    let allRecords = [];
    let offset = undefined;

    do {
      const response = await axios.get(
        "https://api.airtable.com/v0/appVhtDyx0VKlGbhy/Keywords",
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_AIRTABLE_API_KEY}`,
          },
          params: {
            fields: ["Keywords"],
            pageSize: 100,
            offset,
          },
        }
      );
      allRecords = allRecords.concat(response.data.records);
      offset = response.data.offset;
    } while (offset);

    const keywords = allRecords
      .map(r => r.fields.Keywords)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    setAllKeywords(keywords);
    setKeywordsLoaded(true);
    
    // Save to cache
    localStorage.setItem('swiftlore_keywords', JSON.stringify(keywords));
    localStorage.setItem('swiftlore_keywords_time', Date.now().toString());
    
  } catch (error) {
    console.error("Error fetching all keywords:", error);
  } finally {
    setKeywordsLoading(false);
  }
};

  // preload keywords in the background as soon as the page mounts
  useEffect(() => {
    loadKeywordsIfNeeded()
  }, [])
// Background sync for keywords
useEffect(() => {
  const checkForUpdates = async () => {
    const cacheTime = localStorage.getItem('swiftlore_keywords_time');
    // Only check if cache is older than 1 hour
    if (!cacheTime || (Date.now() - parseInt(cacheTime)) > 60 * 60 * 1000) {
      await loadKeywordsIfNeeded();
    }
  };
  
  // Check for updates 30 seconds after page load (non-blocking)
  const timer = setTimeout(checkForUpdates, 30000);
  return () => clearTimeout(timer);
}, []);
  // ===== Pre-fetch events for calendar indicators =====
useEffect(() => {
  const fetchEventsForMonth = async (month, year) => {
    try {
      const response = await axios.get(
        "https://api.airtable.com/v0/appVhtDyx0VKlGbhy/Taylor%20Swift%20Master%20Tracker",
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_AIRTABLE_API_KEY}`,
          },
          params: {
  filterByFormula: `AND(MONTH({DATE}) = ${month}, YEAR({DATE}) = ${year})`,
  fields: ["DATE"],
},
        }
      )
      
      // Create a map of dates that have events (no timezone conversion)
const eventsMap = {}
response.data.records?.forEach(record => {
  const raw = record.fields.DATE
  if (typeof raw === "string") {
    // Airtable date-only fields come back as "YYYY-MM-DD"
    const [y, m, d] = raw.split("-").map(Number)
    const dateKey = `${y}-${pad2(m)}-${pad2(d)}` // e.g. 2019-06-30
    eventsMap[dateKey] = true
  }
})

setDateEventsMap(prev => ({ ...prev, ...eventsMap }))
    } catch (error) {
      console.error("Error fetching calendar events:", error)
    }
  }

  if (showCalendar) {
    fetchEventsForMonth(calendarMonth + 1, calendarYear)
  }
}, [calendarMonth, calendarYear, showCalendar])
useEffect(() => {
  // Only trigger filter when cleared or when MM/DD is valid
  if (monthDay === "" || isCompleteMonthDay(monthDay)) {
    // Small delay to batch state updates
    const timer = setTimeout(() => {
      resetPagination()
    }, 10)
    
    return () => clearTimeout(timer)
  }
}, [monthDay])

useEffect(() => {
  const timer = setTimeout(() => setDebouncedStart(startDateInput), 600)
  return () => clearTimeout(timer)
}, [startDateInput])

useEffect(() => {
  const timer = setTimeout(() => setDebouncedEnd(endDateInput), 600)
  return () => clearTimeout(timer)
}, [endDateInput])
  
// filter keywords list (using dynamic list from Airtable)
const getFilteredKeywords = () => {
  const source = allKeywords.length ? allKeywords : []
  if (!keywordSearchQuery.trim()) return source

  const query = keywordSearchQuery.toLowerCase()
  return source
    .filter((keyword) => keyword.toLowerCase().includes(query))
    .sort((a, b) => {
      const aLower = a.toLowerCase()
      const bLower = b.toLowerCase()
      if (aLower.startsWith(query) && !bLower.startsWith(query)) return -1
      if (!aLower.startsWith(query) && bLower.startsWith(query)) return 1
      return a.localeCompare(b)
    })
}

  // Fetch posts (filters + pagination)
  useEffect(() => {
  const fetchPosts = async () => {
      // If user started typing Month/Day but it's not a complete MM/DD yet, don't fetch yet
      if (monthDay && !isCompleteMonthDay(monthDay)) {
        return
      }

      setLoading(true)

      try {
        const clauses = []

        const startISO = parseMMDDYYYYToISO(debouncedStart)
        const endISO = parseMMDDYYYYToISO(debouncedEnd)

        // inclusive start: DATE >= startISO
        if (startISO) {
          clauses.push(
            `OR(IS_SAME({DATE}, '${startISO}'), IS_AFTER({DATE}, '${startISO}'))`
          )
        }

        // inclusive end: DATE <= endISO
        if (endISO) {
          clauses.push(
            `OR(IS_SAME({DATE}, '${endISO}'), IS_BEFORE({DATE}, '${endISO}'))`
          )
        }

        // month/day MM/DD (any year), only when valid
if (isCompleteMonthDay(monthDay)) {
  const [m, d] = monthDay.split("/")
  const monthNum = parseInt(m, 10)
  const dayNum = parseInt(d, 10)

  clauses.push(
    `AND(MONTH({DATE}) = ${monthNum}, DAY({DATE}) = ${dayNum})`
  )
}

        // keyword filters
        if (filterKeywords.length > 0) {
          const keywordFilters = filterKeywords.map((keyword) => {
            const safe = keyword.replace(/'/g, "\\'")
            return `FIND('${safe}', ARRAYJOIN({KEYWORDS}, ',')) > 0`
          })

          const keywordFormula =
            keywordMatchType === "any"
              ? `OR(${keywordFilters.join(",")})`
              : `AND(${keywordFilters.join(",")})`

          clauses.push(keywordFormula)
        }

        // basic search (when not in "search mode")
        if (searchQuery.trim() && !isSearchMode) {
          const capitalizedQuery =
            searchQuery.trim().charAt(0).toUpperCase() +
            searchQuery.trim().slice(1)

          const searchFormula = `OR(
            FIND('${capitalizedQuery}', {EVENT}) > 0,
            FIND('${capitalizedQuery}', {LOCATION}) > 0,
            FIND('${capitalizedQuery}', {CATEGORY}) > 0,
            FIND('${capitalizedQuery}', ARRAYJOIN({KEYWORDS}, ',')) > 0
          )`

          clauses.push(searchFormula)
        }

        const filterFormula =
          clauses.length === 0
            ? ""
            : clauses.length === 1
            ? clauses[0]
            : `AND(${clauses.join(",")})`

        const isFilterActive = clauses.length > 0
        setIsFilterMode(isFilterActive)

        const currentOffset = offsetHistory[currentOffsetIndex]

const response = await axios.get(
  "https://api.airtable.com/v0/appVhtDyx0VKlGbhy/Taylor%20Swift%20Master%20Tracker",
  {
    headers: {
      Authorization: `Bearer ${import.meta.env.VITE_AIRTABLE_API_KEY}`,
    },
    params: {
  pageSize: clauses.length > 0 ? filterRecordsPerPage : recordsPerPage,
  offset: currentOffset || undefined,
  filterByFormula: filterFormula || undefined,
  sort: [{ field: "DATE", direction: sortOrder }],
},
  }
)

const hasMoreRecords = !!response.data.offset
setHasMore(hasMoreRecords)

if (hasMoreRecords && currentOffsetIndex === offsetHistory.length - 1) {
  setOffsetHistory((prev) => [...prev, response.data.offset])
}

const formattedPosts = response.data.records.map((record) => ({
  id: record.id,
  date: record.fields.DATE
    ? (() => {
        const date = new Date(record.fields.DATE)
        const options = {
          month: "short",
          day: "2-digit",
          year: "numeric",
          timeZone: "UTC",
        }
        return date.toLocaleDateString("en-US", options)
      })()
    : "No date",
  category: record.fields.CATEGORY || "Uncategorized",
  title: record.fields.EVENT || "Untitled Event",
  location: record.fields.LOCATION || "",
  image: record.fields.IMAGE?.[0]?.url || null,
  year: record.fields.DATE ? new Date(record.fields.DATE).getFullYear() : "",
  keywords: record.fields.KEYWORDS || [],
  notes: record.fields.NOTES || null,
}))

setPosts(formattedPosts)
      } catch (error) {
        console.error("Error fetching records:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [
    currentOffsetIndex,
    sortOrder,
    filterKeywords,
    keywordMatchType,
    debouncedStart,
    debouncedEnd,
    monthDay,
    searchQuery,
    offsetHistory,
    isSearchMode,
  ])

  // when paging search results, re-run the search fetch using the current offset
useEffect(() => {
  if (!isSearchMode) return
  if (!searchQuery.trim()) return

  // run the same search logic as submit, but using updated offset
  handleSearch(new Event("submit"), { reset: false })
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [searchOffsetIndex, sortOrder])

  // filter helpers
  const handleSortChange = (order) => {
  userInteractedRef.current = true
  setSortOrder(order)
  resetPagination()
}

  const handleKeywordFilter = (keyword) => {
  userInteractedRef.current = true

  if (filterKeywords.includes(keyword)) {
    setFilterKeywords(filterKeywords.filter((k) => k !== keyword))
  } else {
    setFilterKeywords([...filterKeywords, keyword])
  }
  resetPagination()
}

  const handleMonthDayChange = (value) => {
  userInteractedRef.current = true
  setMonthDay(value)
}

const handleStartDateChange = (value) => {
  userInteractedRef.current = true
  setStartDateInput(value)
  // Only reset pagination when date is complete or cleared
  if (value === "" || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    resetPagination()
  }
}

const handleEndDateChange = (value) => {
  userInteractedRef.current = true
  setEndDateInput(value)
  // Only reset pagination when date is complete or cleared
  if (value === "" || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    resetPagination()
  }
}

  // Search
  const handleSearch = async (e, { reset = true } = {}) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      try {
        setLoading(true)
setIsSearchMode(true)

// reset search pagination ONLY for a fresh search
if (reset) {
  setSearchPage(1)
  setSearchHasMore(false)
  setSearchOffsetHistory([null])
  setSearchOffsetIndex(0)
}

        const searchTerms = searchQuery
          .trim()
          .toLowerCase()
          .split(" ")
          .filter((term) => term.length > 0)

        const searchConditions = searchTerms.map(
          (term) => `OR(
          SEARCH("${term}", LOWER({EVENT})),
          SEARCH("${term}", LOWER({NOTES})),
          SEARCH("${term}", LOWER(ARRAYJOIN({KEYWORDS}, ", ")))
        )`
        )

        const filterFormula =
          searchConditions.length > 1
            ? `AND(${searchConditions.join(", ")})`
            : searchConditions[0]

        const currentSearchOffset = searchOffsetHistory[searchOffsetIndex]

const response = await axios.get(
  "https://api.airtable.com/v0/appVhtDyx0VKlGbhy/Taylor%20Swift%20Master%20Tracker",
  {
    headers: {
      Authorization: `Bearer ${import.meta.env.VITE_AIRTABLE_API_KEY}`,
    },
    params: {
      pageSize: searchRecordsPerPage,
      offset: currentSearchOffset || undefined,
      filterByFormula: filterFormula,
      sort: [{ field: "DATE", direction: sortOrder }],
    },
  }
)

const hasMore = !!response.data.offset
setSearchHasMore(hasMore)

if (hasMore && searchOffsetIndex === searchOffsetHistory.length - 1) {
  setSearchOffsetHistory((prev) => [...prev, response.data.offset])
}

        const formattedResults = response.data.records.map((record) => ({
          id: record.id,
          date: record.fields.DATE
            ? (() => {
                const date = new Date(record.fields.DATE)
                const options = {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                  timeZone: "UTC",
                }
                return date.toLocaleDateString("en-US", options)
              })()
            : "No date",
          category: record.fields.CATEGORY || "Uncategorized",
          title: record.fields.EVENT || "Untitled Event",
          location: record.fields.LOCATION || "",
          image: record.fields.IMAGE?.[0]?.url || null,
          year: record.fields.DATE
            ? new Date(record.fields.DATE).getFullYear()
            : "",
          keywords: record.fields.KEYWORDS || [],
          notes: record.fields.NOTES || null,
        }))

        setSearchResults(formattedResults)
      } catch (error) {
        console.error("Error fetching search results:", error)
        setSearchResults([])
      } finally {
        setLoading(false)
      }
    } else {
      setIsSearchMode(false)
      setSearchResults([])
    }
  }

  const clearSearch = () => {
    setIsSearchMode(false)
    setSearchResults([])
    setSearchQuery("")
  }

  const handleSearchInputChange = (e) => {
  userInteractedRef.current = true
  setSearchQuery(e.target.value)
}

  const handleSearchKeyPress = (e) => {
  if (e.key === "Enter") {
    // let the form onSubmit handle it
    // (prevents double-trigger)
  }
}

  // pagination helpers (timeline)
const handleSearchPreviousPage = () => {
  if (searchOffsetIndex > 0) {
    setSearchOffsetIndex((prev) => prev - 1)
    setSearchPage((prev) => prev - 1)
    window.scrollTo(0, 0)
  }
}

const handleSearchNextPage = () => {
  if (searchHasMore) {
    setSearchOffsetIndex((prev) => prev + 1)
    setSearchPage((prev) => prev + 1)
    window.scrollTo(0, 0)
  }
}

// pagination helpers
const resetPagination = () => {
  // timeline paging
  setPage(1)
  setCurrentOffsetIndex(0)
  setOffsetHistory([null])

  // search paging
  setSearchPage(1)
  setSearchOffsetIndex(0)
  setSearchOffsetHistory([null])
  setSearchHasMore(false)
}

  const handlePreviousPage = () => {
    if (currentOffsetIndex > 0) {
      setCurrentOffsetIndex((prev) => prev - 1)
      setPage((prev) => prev - 1)
      window.scrollTo(0, 0)
    }
  }

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentOffsetIndex((prev) => prev + 1)
      setPage((prev) => prev + 1)
      window.scrollTo(0, 0)
    }
  }

  // Reset ALL filters
  const resetAllFilters = () => {
  setSortOrder("desc")
  setFilterKeywords([])
  setKeywordMatchType("all")
  setKeywordSearchQuery("")
  setStartDateInput("")
  setEndDateInput("")
  setMonthDay("")
  setSearchQuery("")
  setIsSearchMode(false)
  setSearchResults([])
  setShowKeywordDropdown(false)
  resetPagination()

  // also clear saved filters + clean URL
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(TIMELINE_FILTERS_KEY)
    const basePath = window.location.pathname
    window.history.replaceState({}, "", basePath)
  }
}

  // Persist filters + view mode to sessionStorage whenever they change
    useEffect(() => {
    if (typeof window === "undefined") return

    const payload = {
      sortOrder,
      filterKeywords,
      startDateInput,
      endDateInput,
      monthDay,
      searchQuery,
      keywordMatchType,
      viewMode,
    }

    try {
      window.sessionStorage.setItem(TIMELINE_FILTERS_KEY, JSON.stringify(payload))
      updateURLParams()
    } catch (e) {
      console.error("Error saving timeline filters:", e)
    }
  }, [
    sortOrder,
    filterKeywords,
    startDateInput,
    endDateInput,
    monthDay,
    searchQuery,
    keywordMatchType,
    viewMode,
  ])

  // group posts by year in original order
  const groupPostsByYear = (list) => {
    const groups = []
    const map = new Map()
    list.forEach((post) => {
      const year = post.year || "Unknown"
      if (!map.has(year)) {
        const group = { year, posts: [] }
        map.set(year, group)
        groups.push(group)
      }
      map.get(year).posts.push(post)
    })
    return groups
  }

  // render helpers: grid vs compact archive
  const renderGridCards = (items) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 items-start">
      {items.map((post) => (
        <Link
          key={post.id}
          to={`/post_details?id=${post.id}`}
          className="bg-[#ffe8e8] rounded-xl overflow-hidden border border-[#ffcaca] shadow-sm flex flex-col hover:shadow-lg transition-shadow duration-200 cursor-pointer h-full"
        >
          <div className="relative pt-1 flex flex-col">
            {/* date pill */}
            <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 bg-[#b66b6b] text-white text-xs font-medium px-2 py-1 rounded-full z-10">
              {post.date}
            </div>

            {/* title */}
            <div className="px-4 pt-5 pb-1 mt-1">
              <h3 className="text-[#8e3e3e]] font-medium text-sm text-center line-clamp-2">
                {post.title}
              </h3>
            </div>

            {/* image */}
            {post.image && (
              <div className="w-[90%] aspect-[4/3] mx-auto mb-2 rounded-[3%] overflow-hidden flex items-center justify-center">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover object-[center_30%]"
                />
              </div>
            )}
          </div>

          <div className="px-4 pb-4 pt-2 flex flex-col flex-grow border-t border-[#ffcaca]">
            {post.notes && (
              <div className="text-[#4b4b63] text-xs mb-2 line-clamp-4 whitespace-pre-line">
                {post.notes}
              </div>
            )}

            <div className="mt-auto">
              <div className="flex flex-wrap gap-2">
                {post.keywords?.map((keyword, index) => (
                  <span
                    key={index}
                    className="bg-[#8a9ac7] text-white text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#6b7db3] transition-colors"
                    onClick={(e) => handleTagClick(e, keyword)}
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )

  const renderCompactArchive = (items) => {
    const groups = groupPostsByYear(items)
    return (
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.year}>
            <h3 className="text-sm font-semibold text-[#8e3e3e] mb-1 mt-2">
              {group.year}
            </h3>
            <div className="space-y-2">
              {group.posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/post_details?id=${post.id}`}
                  className="flex items-start gap-3 bg-[#ffe8e8] border border-[#ffcaca] rounded-lg px-3 py-2 hover:shadow-md transition-shadow duration-150 cursor-pointer"
                >
                  {/* date pill */}
                  <div className="shrink-0">
                    <div className="bg-white text-[#b91c1c] text-[11px] font-medium px-2 py-1 rounded-full text-center min-w-[88px]">
                      {post.date}
                    </div>
                  </div>

                  {/* text content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
                      <h3 className="text-[#b91c1c] font-medium text-sm line-clamp-1">
                        {post.title}
                      </h3>
                      {post.location && (
                        <span className="text-[11px] text-[#6b7db3] line-clamp-1">
                          {post.location}
                        </span>
                      )}
                    </div>

                    {post.notes && (
                      <p className="mt-1 text-[11px] text-[#6b7db3] line-clamp-2 whitespace-pre-line">
                        {post.notes}
                      </p>
                    )}

                    {/* tags row */}
                    {post.keywords?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {post.keywords.slice(0, 4).map((keyword, index) => (
                          <span
                            key={index}
                            className="bg-[#8a9ac7] text-white text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#6b7db3] transition-colors"
                            onClick={(e) => handleTagClick(e, keyword)}
                          >
                            {keyword}
                          </span>
                        ))}
                        {post.keywords.length > 4 && (
                          <span className="text-[10px] text-gray-500">
                            +{post.keywords.length - 4} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }
// ===== Enhanced Calendar Modal =====
const CalendarModal = () => {
  if (!showCalendar) return null

  const calendarDays = generateCalendar()

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={() => setShowCalendar(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 animate-in fade-in-zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Quick Actions Bar */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={jumpToToday}
            className="flex-1 text-xs py-1 h-auto"
          >
            <Clock size={12} className="mr-1" />
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={jumpToThisMonth}
            className="flex-1 text-xs py-1 h-auto"
          >
            <Zap size={12} className="mr-1" />
            This Month
          </Button>
        </div>

        {/* Calendar Header */}
<div className="flex items-center justify-between mb-2">
  <Button
    variant="ghost"
    size="sm"
    onClick={() => navigateCalendarMonth("prev")}
    className="p-2 hover:bg-[#f8d7da] transition-colors"
  >
    <ChevronLeft size={18} className="text-[#8e3e3e]" />
  </Button>

  <div className="text-lg font-semibold text-[#8e3e3e] flex items-center gap-2">
    <Star size={16} className="text-[#ffd700]" fill="#ffd700" />
    {monthNames[calendarMonth]} {calendarYear}
    <Star size={16} className="text-[#ffd700]" fill="#ffd700" />
  </div>

  <Button
    variant="ghost"
    size="sm"
    onClick={() => navigateCalendarMonth("next")}
    className="p-2 hover:bg-[#f8d7da] transition-colors"
  >
    <ChevronRight size={18} className="text-[#8e3e3e]" />
  </Button>
</div>

{/* Month / Year dropdowns */}
<div className="flex items-center justify-center gap-2 mb-4">
  {/* Month select */}
  <select
    className="border border-[#e3b0b0] rounded-full px-3 py-1 text-xs text-[#8e3e3e] bg-white"
    value={calendarMonth}
    onChange={(e) => setCalendarMonth(Number(e.target.value))}
  >
    {monthNames.map((name, idx) => (
      <option key={name} value={idx}>
        {name}
      </option>
    ))}
  </select>

  {/* Year select */}
  <select
    className="border border-[#e3b0b0] rounded-full px-3 py-1 text-xs text-[#8e3e3e] bg-white"
    value={calendarYear}
    onChange={(e) => setCalendarYear(Number(e.target.value))}
  >
    {Array.from(
      { length: new Date().getFullYear() + 5 - 2006 + 1 },
      (_, i) => 2006 + i
    ).map((year) => (
      <option key={year} value={year}>
        {year}
      </option>
    ))}
  </select>
</div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-semibold text-[#6b7db3] py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
<div className="grid grid-cols-7 gap-1">
  {calendarDays.map((day, index) => {
    const isEmpty = !day

    // figure out if this day is the selected MM/DD
    let isSelected = false
    if (!isEmpty && monthDay && monthDay.includes("/")) {
      const [selMonthStr, selDayStr] = monthDay.split("/")
      const selMonth = parseInt(selMonthStr, 10)
      const selDay = parseInt(selDayStr, 10)
      isSelected = day === selDay && (calendarMonth + 1) === selMonth
    }

    const baseClasses =
      "relative h-8 rounded-lg text-sm font-medium transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center"

    const visibilityClasses = isEmpty ? "invisible" : ""

    const stateClasses = isSelected
      ? "bg-[#8e3e3e] text-white shadow-md scale-105"
      : "bg-white/80 text-[#8e3e3e] hover:bg-[#f8d7da]"

    const borderClasses = hasEvents(day)
      ? "border-2 border-[#e3b0b0]"
      : "border border-transparent"

    return (
      <button
        key={index}
        onClick={() => handleDateSelect(day)}
        disabled={isEmpty}
        className={`${baseClasses} ${visibilityClasses} ${stateClasses} ${borderClasses}`}
      >
        {/* Day number */}
        {!isEmpty && (
          <span className="relative z-10">
            {day}
          </span>
        )}

        {/* Event indicator dot */}
        {hasEvents(day) && !isEmpty && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#8e3e3e] rounded-full" />
        )}
      </button>
    )
  })}
</div>

        {/* Action Buttons */}
        <div className="flex gap-2 justify-center mt-4">
          <Button
            variant="secondary"
            onClick={() => setShowCalendar(false)}
            className="rounded-full px-6 flex-1"
          >
            Close
          </Button>
          <Button
            onClick={jumpToToday}
            className="rounded-full px-6 flex-1 bg-[#8e3e3e] hover:bg-[#7a3434]"
          >
            Go to Today
          </Button>
        </div>
      </div>
    </div>
  )
}
  
  return (
       <div className="bg-[#e6edf7] py-8 overflow-x-hidden">
               {/* SEO helper for crawlers, hidden visually */}
      <div className="sr-only" aria-hidden="true">
        <h1>Taylor Swift Full Timeline Archive</h1>
        <p>
          Browse the complete Swift-Lore archive of Taylor Swift events with
          filters for dates, keywords, albums, tours, locations, and deep-cut
          easter eggs.
        </p>
      </div>
         
      {/* ADD INTRODUCTORY TEXT HERE - New Section */}
      <div className="max-w-6xl mx-auto px-4 mb-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-[#8e3e3e] mb-4">
            Swift-Lore
          </h1>
          <p className="text-[#6b7db3] text-lg max-w-2xl mx-auto">
            Welcome to Swift-Lore, a fan-made interactive timeline exploring 
            Taylor Swift's career history, releases, Easter eggs, and more. 
            Browse events, filter by keywords, or search for specific moments.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 mb-6">
        <div className="relative flex flex-wrap gap-2 py-4 items-center overflow-visible">
          {/* Sort By */}
          <div className="relative">
            <button
              className="flex items-center justify-between bg.white text-[#6b7db3] border border-[#6b7db3] rounded-full px-4 py-1.5 text-sm min-w-[120px] bg-white"
              onClick={() =>
                handleSortChange(sortOrder === "asc" ? "desc" : "asc")
              }
            >
              <span>
  {sortOrder === "asc" ? "Oldest → Newest" : "Newest → Oldest"}
</span>
              <span className="ml-2">▼</span>
            </button>
          </div>

          {/* Filter Keywords dropdown */}
          <div className="relative">
            <button
              className="flex items-center justify-between bg-white text-[#6b7db3] border border-[#6b7db3] rounded-full px-4 py-1.5 text-sm min-w-[150px]"
              onClick={() => {
                const next = !showKeywordDropdown
                setShowKeywordDropdown(next)
              }}
            >
              <span>
                {filterKeywords.length > 0
                  ? `${filterKeywords.length} selected`
                  : "Filter by keyword"}
              </span>
              <span className="ml-2">▼</span>
            </button>

            {showKeywordDropdown && (
    <div className="absolute left-0 top-full mt-1 w-[90vw] sm:w-64 max-w-[90vw] bg-white border border-[#6b7db3] rounded-lg shadow-lg z-50 max-h-[60vh] overflow-y-auto">
    <div className="p-2">
                  {keywordsLoading && allKeywords.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-gray-500">
                      Loading keywords…
                    </div>
                  ) : (
                    <>
                      {/* Keyword search input */}
                      <div className="relative mb-2">
                        <input
                          type="text"
                          placeholder="Search keywords..."
                          className="w-full bg-white text-[#6b7db3] border border-[#6b7db3] rounded-full px-3 py-1.5 text-sm"
                          value={keywordSearchQuery}
                          onChange={(e) =>
                            setKeywordSearchQuery(e.target.value)
                          }
                        />
                      </div>

                      {/* Any / All toggle */}
                      {filterKeywords.length > 0 && (
                        <div className="mb-2 p-2 bg-[#e6edf7] rounded">
                          <div className="flex items-center gap-4">
                            <label className="flex items-center text-sm">
                              <input
                                type="radio"
                                name="matchType"
                                value="any"
                                checked={keywordMatchType === "any"}
                                onChange={(e) => {
                                  setKeywordMatchType(e.target.value)
                                  resetPagination()
                                }}
                                className="mr-1"
                              />
                              Has Any
                            </label>
                            <label className="flex items-center text-sm">
                              <input
                                type="radio"
                                name="matchType"
                                value="all"
                                checked={keywordMatchType === "all"}
                                onChange={(e) => {
                                  setKeywordMatchType(e.target.value)
                                  resetPagination()
                                }}
                                className="mr-1"
                              />
                              Has All
                            </label>
                          </div>
                        </div>
                      )}

                      <button
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#e6edf7] rounded mb-1"
                        onClick={() => {
                          setFilterKeywords([])
                          setShowKeywordDropdown(false)
                          setKeywordSearchQuery("")
                          resetPagination()
                        }}
                      >
                        Clear All Filters
                      </button>

                      <div className="max-h-[50vh] overflow-y-auto">
                        {getFilteredKeywords().map((keyword, index) => (
                          <div
                            key={index}
                            className="flex items-center px-3 py-2"
                          >
                            <input
                              type="checkbox"
                              id={`keyword-${index}`}
                              checked={filterKeywords.includes(keyword)}
                              onChange={() => handleKeywordFilter(keyword)}
                              className="mr-2"
                            />
                            <label
                              htmlFor={`keyword-${index}`}
                              className="text-sm cursor-pointer"
                            >
                              {keyword}
                            </label>
                          </div>
                        ))}

                        {getFilteredKeywords().length === 0 &&
                          keywordSearchQuery && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              No keywords found matching "{keywordSearchQuery}"
                            </div>
                          )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Start Date */}
          <div className="relative">
            <input
              type="text"
              placeholder="Start Date (MM/DD/YYYY)"
              className="bg-white text-[#6b7db3] border border-[#6b7db3] rounded-full px-4 py-1.5 text-sm min-w-[170px]"
              value={startDateInput}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </div>

          {/* End Date */}
          <div className="relative">
            <input
              type="text"
              placeholder="End Date (MM/DD/YYYY)"
              className="bg-white text-[#6b7db3] border border-[#6b7db3] rounded-full px-4 py-1.5 text-sm min-w-[170px]"
              value={endDateInput}
              onChange={(e) => handleEndDateChange(e.target.value)}
            />
          </div>

                    {/* Month/Day */}
<div className="relative">
  <input
    type="text"
    placeholder="Month/Day (MM/DD)"
    className="bg-white text-[#6b7db3] border border-[#6b7db3] rounded-full px-4 py-1.5 text-sm min-w-[150px]"
    value={monthDay}
    onChange={(e) => handleMonthDayChange(e.target.value)}
  />
</div>

{/* Standalone Calendar Button */}
<div className="relative">
  <button
    onClick={() => setShowCalendar(true)}
    className="flex items-center gap-2 bg-white text-[#8e3e3e] border border-[#8e3e3e] rounded-full px-4 py-1.5 text-sm hover:bg-[#f8d7da] transition-colors"
    type="button"
  >
    <Calendar size={16} />
    <span>Calendar</span>
  </button>
</div>

{/* ✅ ADD THIS: Date Calculator Button */}
<div className="relative">
  <button
    onClick={() => setShowDateCalc(true)}
    className="flex items-center gap-2 bg-white text-[#8e3e3e] border border-[#8e3e3e] rounded-full px-4 py-1.5 text-sm hover:bg-[#f8d7da] transition-colors"
    type="button"
  >
    <Clock size={16} />
    <span>Date Calc</span>
  </button>
</div>
          
          {/* Search */}
          <div className="relative flex-grow min-w-[200px]">
            <form onSubmit={handleSearch} className="relative ml-2">
              <input
                type="text"
                placeholder="Search any keywords"
                className="w-full rounded-full py-1.5 pl-10 pr-4 text-sm bg-white border border-[#6b7db3] text-[#6b7db3]"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyPress}
              />
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="#6b7db3"
                >
                  <path d="M10 2a8 8 0 105.293 14.293l4.707 4.707 1.414-1.414-4.707-4.707A8 8 0 0010 2zm0 2a6 6 0 110 12A6 6 0 0110 4z" />
                </svg>
              </div>
            </form>
          </div>

          {/* View mode toggle */}
          <div className="flex items-center gap-1 ml-auto">
            <button
              type="button"
              className={`px-3 py-1 text-xs rounded-full border ${
                viewMode === "grid"
                  ? "bg-[#c25e5e] text-white border-[#c25e5e]"
                  : "bg-white text-[#6b7db3] border-[#6b7db3]"
              }`}
              onClick={() => setViewMode("grid")}
            >
              Grid
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-xs rounded-full border ${
                viewMode === "compact"
                  ? "bg-[#c25e5e] text-white border-[#c25e5e]"
                  : "bg-white text-[#6b7db3] border-[#6b7db3]"
              }`}
              onClick={() => setViewMode("compact")}
            >
              Compact
            </button>
          </div>

          {/* Reset Filters button */}
          <button
            className="flex items-center bg-white text-[#b91c1c] border border-[#b91c1c] rounded-full px-4 py-1.5 text-sm"
            onClick={resetAllFilters}
          >
            Reset filters
          </button>

          {/* Clear keyword filters button (when some selected) */}
          {filterKeywords.length > 0 && (
            <div className="relative">
              <button
                className="flex items-center justify-between bg-[#b91c1c] text-white rounded-full px-4 py-1.5 text-sm"
                onClick={() => {
                  setFilterKeywords([])
                  resetPagination()
                }}
              >
                Clear {filterKeywords.length} Filter
                {filterKeywords.length !== 1 ? "s" : ""}
                <span className="ml-2">×</span>
              </button>
            </div>
          )}
        </div>
      </div>
         
                  {/* Ad block */}
{import.meta.env.PROD && !loading && (posts.length > 0 || searchResults.length > 0) && (
  <AdSlot
  variant="leaderboard" 
  maxWidthClass="max-w-6xl"
  className="mb-2"
/>
)}

      {/* Selected keywords chips */}
      {filterKeywords.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 mb-4">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm text-[#6b7db3] font-medium">
              Has {keywordMatchType === "all" ? "All" : "Any"}:
            </span>
            {filterKeywords.map((keyword, index) => (
              <div
                key={index}
                className="bg-[#8a9ac7] text-white text-xs px-3 py-1 rounded-full flex items-center cursor-pointer hover:bg-[#6b7db3]"
                onClick={() => {
                  setFilterKeywords(filterKeywords.filter((k) => k !== keyword))
                  resetPagination()
                }}
              >
                {keyword}
                <span className="ml-1 text-xs">×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main content / grids */}
      {!loading && (
        <div className="max-w-6xl mx-auto px-4">
          {isSearchMode ? (
            <>
              {/* Search results header */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-serif text-[#8e3e3e] mb-2">
                  Search Results for "{searchQuery}"
                </h2>
                <p className="text-[#8e3e3e] text-lg mb-4">
                  {searchResults.length} result
                  {searchResults.length !== 1 ? "s" : ""} found
                </p>
                <button
                  onClick={clearSearch}
                  className="bg-[#b91c1c] text-white rounded-full px-4 py-1.5 text-sm mb-6"
                >
                  Clear Search
                </button>
              </div>

              {/* Search results list */}
              {searchResults.length === 0 ? (
  <div className="text-center py-12 text-[#b91c1c]">
    No results found for "{searchQuery}"
  </div>
) : (
  <>
    {viewMode === "grid"
      ? renderGridCards(searchResults)
      : renderCompactArchive(searchResults)}

    {(searchResults.length > searchRecordsPerPage || searchHasMore || searchOffsetIndex > 0)
&& (
      <div className="max-w-6xl mx-auto px-4 my-8 flex justify-center items-center gap-2">
        <span
          className={`text-sm ${
            searchPage > 1 ? "text-[#bb6d6d] cursor-pointer" : "text-[#bb6d6d]/50"
          }`}
          onClick={searchPage > 1 ? handleSearchPreviousPage : undefined}
        >
          Previous Page
        </span>

        <button
          className={`w-8 h-8 flex items-center justify-center rounded-full border border-[#bb6d6d] ${
            searchPage > 1
              ? "bg-[#e6edf7] text-[#bb6d6d]"
              : "bg-[#e6edf7]/50 text-[#bb6d6d]/50"
          }`}
          onClick={searchPage > 1 ? handleSearchPreviousPage : undefined}
          disabled={searchPage <= 1}
        >
          &lt;
        </button>

        <div className="mx-2 text-[#bb6d6d]">Page {searchPage}</div>

        <button
          className={`w-8 h-8 flex items-center justify-center rounded-full border border-[#bb6d6d] ${
            searchHasMore
              ? "bg-[#e6edf7] text-[#bb6d6d]"
              : "bg-[#e6edf7]/50 text-[#bb6d6d]/50"
          }`}
          onClick={searchHasMore ? handleSearchNextPage : undefined}
          disabled={!searchHasMore}
        >
          &gt;
        </button>

        <span
          className={`text-sm ${
            searchHasMore ? "text-[#bb6d6d] cursor-pointer" : "text-[#bb6d6d]/50"
          }`}
          onClick={searchHasMore ? handleSearchNextPage : undefined}
        >
          Next Page
        </span>
      </div>
    )}
  </>
)}

            </>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-[#b91c1c]">
              No posts found matching your criteria. Try different filters.
            </div>
          ) : viewMode === "grid" ? (
            renderGridCards(posts)
          ) : (
            renderCompactArchive(posts)
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading &&
  !isSearchMode &&
  (posts.length > (isFilterMode ? filterRecordsPerPage : recordsPerPage) || hasMore) && (
          <div className="max-w-6xl mx-auto px-4 my-8 flex justify-center items-center gap-2">
            <span
              className={`text-sm ${
                page > 1 ? "text-[#bb6d6d] cursor-pointer" : "text-[#bb6d6d]/50"
              }`}
              onClick={page > 1 ? handlePreviousPage : undefined}
            >
              Previous Page
            </span>
            <button
              className={`w-8 h-8 flex items-center justify-center rounded-full border border-[#bb6d6d] ${
                page > 1
                  ? "bg-[#e6edf7] text-[#bb6d6d]"
                  : "bg-[#e6edf7]/50 text-[#bb6d6d]/50"
              }`}
              onClick={page > 1 ? handlePreviousPage : undefined}
              disabled={page <= 1}
            >
              &lt;
            </button>
            <div className="mx-2 text-[#bb6d6d]">Page {page}</div>
            <button
              className={`w-8 h-8 flex items-center justify-center rounded-full border border-[#bb6d6d] ${
                hasMore
                  ? "bg-[#e6edf7] text-[#bb6d6d]"
                  : "bg-[#e6edf7]/50 text-[#bb6d6d]/50"
              }`}
              onClick={hasMore ? handleNextPage : undefined}
              disabled={!hasMore}
            >
              &gt;
            </button>
            <span
              className={`text-sm ${
                hasMore ? "text-[#bb6d6d] cursor-pointer" : "text-[#bb6d6d]/50"
              }`}
              onClick={hasMore ? handleNextPage : undefined}
            >
              Next Page
            </span>
          </div>
        )}

            {/* View On This Day Button */}
      <div className="max-w-6xl mx-auto px-4 mt-16">
        <button
          className="w-full bg-[#c25e5e] text-white py-3 rounded-full font-medium text-white"
          onClick={() => {
            navigate("/timeline")
            window.scrollTo(0, 0)
          }}
        >
          View On This Day
        </button>
      </div>
      
      {/* Calendar Modal */}
<CalendarModal />

{/* Date Calculator Modal */}
{showDateCalc && (
  <DateCalculatorModal onClose={() => setShowDateCalc(false)} />
)}
      
      <br />
    </div>
  )
}
